import React, { KeyboardEvent, useRef, useEffect } from 'react';
import { useChatWidgetContext, StopGenerationButton, ContextChipList } from 'features/nekst-widgets';
import { useLlmTokenUsageService } from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';

export default function FullPageComposer() {
  const {
    sendMessage,
    stopGeneration,
    isStreaming,
    inputText,
    setInputText,
    selectedContext,
    removeContext,
  } = useChatWidgetContext();

  const usageService = useLlmTokenUsageService();
  const { data: usageData } = useDataLoaderNew(usageService.getUsage);
  const isLimitReached = usageData ? usageData.percentUsed >= 100 : false;
  const isInputDisabled = isStreaming || isLimitReached;

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 128)}px`;
    }
  }, [inputText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.setProperty('border', 'none', 'important');
      textareaRef.current.style.setProperty('outline', 'none', 'important');
      textareaRef.current.style.setProperty('background-color', 'transparent', 'important');
    }
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && isStreaming) {
      e.preventDefault();
      stopGeneration();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const canSend = !isInputDisabled && inputText.trim().length > 0;

  return (
    <div
      className="flex flex-col rounded-[16px] border border-gray-200 bg-white transition-colors focus-within:border-gray-300"
      style={{ padding: '8px 4px' }}
    >
      <textarea
        ref={textareaRef}
        className="w-full resize-none font-[inherit] placeholder:text-gray-400"
        style={{ minHeight: '56px', maxHeight: '128px', padding: '8px 16px 12px', fontSize: '14px', lineHeight: '1.5', backgroundColor: 'transparent', border: 'none', outline: 'none' }}
        placeholder={isLimitReached ? 'Monthly token limit reached' : 'Send a message...'}
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isInputDisabled}
        rows={1}
        data-testid="chat-message-input"
      />
      <div className="flex items-center justify-between" style={{ padding: '0 8px 4px' }}>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full border-none bg-transparent text-gray-700 cursor-pointer flex-shrink-0"
          style={{ width: '34px', height: '34px', fontSize: '20px', lineHeight: 1, padding: 0 }}
          disabled
          title="Add attachment"
        >
          +
        </button>
        {selectedContext.length > 0 && (
          <div className="flex-1 min-w-0">
            <ContextChipList context={selectedContext} onRemove={removeContext} />
          </div>
        )}
        <StopGenerationButton variant="icon" />
        {!isStreaming && (
          <button
            type="button"
            className={`inline-flex items-center justify-center rounded-full border-none ${
              canSend
                ? 'bg-gray-900 text-white cursor-pointer'
                : 'bg-gray-200 text-gray-400 cursor-default'
            }`}
            style={{ width: '34px', height: '34px', fontSize: '20px', lineHeight: 1, padding: 0 }}
            onClick={sendMessage}
            disabled={!canSend}
            data-testid="chat-send-button"
            title="Send message"
          >
            &#8593;
          </button>
        )}
      </div>
    </div>
  );
}
